import apiClient from './api';

/**
 * Profile Service
 *
 * API calls for user profile management
 */

export interface ProfileStats {
  postsCreated: number;
  postsPublished: number;
  channelsConnected: number;
  teamMembers: number;
}

export interface ActivityItem {
  id: number;
  /**
   * Task #182 F2: 'profile_updated' was never emitted by the backend
   * (`profile.routes.ts`'s own activity union excludes it — dormant since
   * introduction) and is intentionally NOT included here. If a future
   * backend change wires it up, add it back here AND to the exhaustive
   * switches in `Profile.tsx` (`getActivityDotColor`/`composeActivityMessage`)
   * — the `never` guards there will fail to compile until you do, by design.
   */
  type:
    | 'post_created'
    | 'post_published'
    | 'post_failed'
    | 'post_partial'
    | 'post_missed'
    | 'channel_connected';
  /** Caption first line (never post.title, which is not user-facing) — present for post_created/published/partial/missed/failed */
  postTitle?: string;
  /** Connected channel's display name (username or platform fallback) — present for channel_connected */
  channelName?: string;
  /** Platform enum value (e.g. "INSTAGRAM") — present for channel_connected */
  platform?: string;
  timestamp: string;
}

export interface ActivityPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export interface ActivityResponse {
  items: ActivityItem[];
  pagination: ActivityPagination;
}

export interface UpdateProfileData {
  fullName?: string;
  avatar?: string;
  timezone?: string | null;
}

export const profileService = {
  /**
   * Get profile statistics
   */
  getStats: async (): Promise<ProfileStats> => {
    const response = await apiClient.get('/api/profile/stats');
    return response.data.data;
  },

  /**
   * Get recent activity with pagination
   */
  getActivity: async (page = 1, limit = 5): Promise<ActivityResponse> => {
    const response = await apiClient.get(`/api/profile/activity?page=${page}&limit=${limit}`);
    return response.data.data;
  },

  /**
   * Update profile information
   */
  updateProfile: async (data: UpdateProfileData): Promise<void> => {
    await apiClient.put('/api/profile', data);
  },
};

export default profileService;
